import React from "react";

import classnames from "classnames";
import isIntuit from "Intuit/isIntuit";
import { capitalize } from "lodash";
import { connect } from "react-redux";
import _ from "underscore";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import formatCurrency from "Utils/formatCurrency";
import { planMetalTierToNumber } from "Utils/plansUtils";
import simplifyPlanName from "Utils/simplifyPlanName";
import writtenNumber from "written-number";

import { Collapse } from "Cobalt/Collapse";
import { Table, TH, TD, TR, ExpandingRow } from "Cobalt/Table";
import { Card, CardSection } from "Common/Card";
import { getCart, getCartMedicalPlanIds, addPackageToCart } from "Common/CartService";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import InfoPopover from "Common/InfoPopover";
import { getPlans } from "Common/PlanLoader/Selectors";
import { MoreDetailsButton, MedicalPlanDetailsModal } from "Common/plans/MedicalPlanDetailsModal";
import { SizeSwitch } from "Common/Responsive";
import Button from "Theme/Button";

import type { MedicalPlanPrice, Cart } from "Utils/types";

import makeBem from "Utils/makeBem";

const cardBem = makeBem("AddMedicalPlansCard");
const toggleBem = makeBem("AddMedicalPlansToggle");
const contentBem = makeBem("AddMedicalPlansContent");
const ANALYTICS_LOCATION = "shopping_cart";

type Props = {
  cart: Cart;
  cartMedicalPlanIds: Array<string>;
  carrierMax: number;
  updateCart: (arg0: Array<string>) => void;
  allPlans: Array<MedicalPlanPrice>;
  medicalPlanPackagePlans: Array<MedicalPlanPrice>;
};

class AddMedicalPlansToggle extends React.Component<
  Props,
  {
    showDropdown: boolean;
  }
> {
  constructor() {
    super();
    this.state = { showDropdown: false };
    this.toggleShow = this.toggleShow.bind(this);
  }

  toggleShow = () => {
    this.setState({ showDropdown: !this.state.showDropdown });
  };

  render() {
    if (this.props.medicalPlanPackagePlans?.length === 0) {
      return null;
    }

    return (
      <Card p={0} mb={0} mt={0} className={classnames(cardBem())}>
        <CardSection
          className={classnames(toggleBem(), {
            [toggleBem(null, "intuit")]: isIntuit(),
          })}
          onClick={() => {
            AnalyticsTracking.trackEvent("add_additional_medical_plans_clicked", { location: ANALYTICS_LOCATION });

            this.toggleShow();
          }}
        >
          <Button
            classicProps={{ color: "link", style: { padding: 0 } }}
            intuitProps={{ color: "link", style: { padding: 0 } }}
            cobaltProps={{ color: "tertiary", style: { padding: "8px 0" } }}
          >
            <SizeSwitch xs="+ Additional Medical Plan" sm="Add an Additional Medical Plan" />
          </Button>
          <Div className={classnames("flex-col-2")} style={{ textAlign: "right" }}>
            <Icon icon={this.state.showDropdown ? "angle-up" : "angle-down"} size="lg" />
          </Div>
        </CardSection>
        <Collapse in={this.state.showDropdown}>
          <div>
            <AddMedicalPlansDropdownDumb toggleShow={this.toggleShow} {...this.props} />
          </div>
        </Collapse>
      </Card>
    );
  }
}

type AddMedicalPlansDropdownProps = {
  cart: Cart;
  cartMedicalPlanIds: Array<string>;
  allPlans: Array<MedicalPlanPrice>;
  carrierMax: number;
  medicalPlanPackagePlans: Array<MedicalPlanPrice>;
  updateCart: (arg0: Array<string>) => void;
};

class AddMedicalPlansDropdownDumb extends React.Component<AddMedicalPlansDropdownProps, AddMedicalPlansDropdownState> {
  constructor(props) {
    super(props);
  }

  handleAddSelectedPlan = (planId) => {
    const { allPlans, updateCart, cartMedicalPlanIds } = this.props;
    // Combine the newly selected plan ids and the cart plan ids, then sort by
    // metal tier. This helps, but does not guarantee, that we set the cart plans
    // in the same order they are displayed to the user.
    const allSelectedPlanIds = cartMedicalPlanIds.concat([planId]).sort((a, b) => {
      const planA = allPlans.find((plan) => plan.id === a);
      const planB = allPlans.find((plan) => plan.id === b);
      return planMetalTierToNumber(planA) - planMetalTierToNumber(planB);
    });

    const allSelectedPlans = allSelectedPlanIds.map((planId) => allPlans.find((plan) => plan.id === planId));

    const selectionPlanTypes = [...new Set(allSelectedPlans.map((plan) => plan.plan_type))];

    const multiplePlansOffered = allSelectedPlans.length > 1;
    const planMix = selectionPlanTypes.length > 1 ? "Mixed Plans" : selectionPlanTypes[0] === "HMO" ? "All HMO" : "All PPO";

    AnalyticsTracking.trackEvent("added_additional_medical_plans", {
      location: ANALYTICS_LOCATION,
      multiple_plans_offered: multiplePlansOffered ? "Yes" : "No",
      ...(multiplePlansOffered ? { plan_mix: planMix } : {}),
    });

    updateCart(allSelectedPlanIds);
  };

  AddPlanButton = (plan) => {
    const { cart, carrierMax } = this.props;
    const showDisabled = cart.medical.length >= carrierMax;

    return showDisabled ? (
      <InfoPopover
        infoText={`You can only offer up to ${writtenNumber(carrierMax)} plans from this package.`}
        placement="top"
        displayComponent={
          <Button color="gray" disabled>
            Add Plan
          </Button>
        }
      />
    ) : (
      <Button color="gray" onClick={() => this.handleAddSelectedPlan(plan.id)}>
        Add Plan
      </Button>
    );
  };

  DesktopTable = () => {
    const { medicalPlanPackagePlans } = this.props;
    return (
      <Table>
        <thead>
          <TR>
            <TH className="w-[130px]">Carrier</TH>
            <TH className="w-[146px]">Tier</TH>
            <TH className="w-[146px]">Network Type</TH>
            <TH className="w-[146px]">Deductible</TH>
            <TH className="w-[146px] sm:max-lg:hidden">Doctor Visits</TH>
            <TH>Plan Details</TH>
            <TH colSpan={2} className="text-right pr-8">
              Average Cost / Employee
            </TH>
          </TR>
        </thead>
        <tbody>
          {medicalPlanPackagePlans.map((plan, i) => (
            <TR key={i}>
              <TD className="font-normal">{plan.carrier}</TD>
              <TD>{capitalize(plan.metal_tier)}</TD>
              <TD>{plan.plan_type}</TD>
              <TD>{formatCurrency(plan.deductible)}</TD>
              <TD className="sm:max-lg:hidden">{formatCurrency(plan.estimated_costs.pcp_visit)}</TD>
              <TD>
                <MoreDetailsButton plan={plan} showIcon={false} />
                <MedicalPlanDetailsModal plan={plan} isSampleQuote={false} inCart isAlternateCart />
              </TD>
              <TD className="text-right">{formatCurrency(plan.average_monthly_premium)}/mo</TD>
              <TD className="text-right">{this.AddPlanButton(plan)}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    );
  };

  MobileTable = () => {
    const { medicalPlanPackagePlans } = this.props;
    return (
      <Table>
        <thead>
          <TR>
            <TH colSpan={2}>Plan Name</TH>
            <TH />
          </TR>
        </thead>
        <tbody>
          {medicalPlanPackagePlans.map((plan, i) => (
            <ExpandingRow
              key={i}
              colspan="4"
              mainContent={
                <TD colSpan={2}>
                  <Row>
                    <Col xs={6} className="text-ellipsis">
                      {simplifyPlanName(plan)}
                    </Col>
                    <Col xs={6} className="text-right">
                      {this.AddPlanButton(plan)}
                    </Col>
                  </Row>
                </TD>
              }
              expandingContent={
                <>
                  <TR>
                    <TD>Doctor Visits</TD>
                    <TD className="text-right">{formatCurrency(plan.estimated_costs.pcp_visit)}</TD>
                    <TD className="text-right" />
                  </TR>
                  <TR>
                    <TD>Average Cost Per Employee</TD>
                    <TD className="text-right">{formatCurrency(plan.average_monthly_premium)}/mo</TD>
                    <TD />
                  </TR>
                  <TR>
                    <TD colSpan={2}>
                      <MoreDetailsButton plan={plan} />
                      <MedicalPlanDetailsModal plan={plan} isSampleQuote={false} inCart isAlternateCart />
                    </TD>
                    <TD className="text-right" />
                  </TR>
                </>
              }
            />
          ))}
        </tbody>
      </Table>
    );
  };

  render() {
    return (
      <CardSection p={2} className={classnames(contentBem())}>
        <SizeSwitch xs={this.MobileTable()} sm={this.DesktopTable()} />
      </CardSection>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const cart = getCart(state);
  const cartMedicalPlanIds = getCartMedicalPlanIds(state);
  const allPlans = getPlans(state);
  const medicalPlanOptionIds = (state.relatedCartPlanIds || []).filter((n) => !cartMedicalPlanIds.includes(n));

  const medicalPlanPackagePlans = _.sortBy(
    _.filter(allPlans, (plan) => _.find(medicalPlanOptionIds, (id) => id == plan?.id)),

    (p) => planMetalTierToNumber(p),
  );

  return {
    cart,
    cartMedicalPlanIds,
    allPlans,
    carrierMax: ownProps.carrierMax,
    medicalPlanPackagePlans,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  updateCart: (planIds) => {
    dispatch(addPackageToCart(planIds));
  },
});

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const AddMedicalPlansBox = withRedux(AddMedicalPlansToggle);

export default AddMedicalPlansBox;
